import validator from 'email-validator';
import noop from 'lodash/noop';
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Button, VARIATION } from '@zocdoc-frontend-common/components-button';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { negativeMedium, slate30 } from '@zocdoc-mezzanine/foundations-colors';
import {
    paragraphMedium,
    labelSmall,
} from '@zocdoc-mezzanine/foundations-typography';
import {
    withCustomMetrics,
    MetricsClickable,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc/frontend-metrics';
import { SignInWithEmailMetrics } from './constants';

const ENTER_KEY = 13;

/**
 *
 * @param {string} className
 * @param {function} onSubmitEmail
 * @param {function} sendMetric
 */
const BaseSignInWithEmail = ({
    className,
    onSubmitEmail = noop,
    sendMetric,
}) => {
    const [email, setEmail] = useState('');
    const [isInvalidEmail, setIsInvalidEmail] = useState(false);
    const [shouldValidateEmail, setShouldValidateEmail] = useState(true);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');

    const onSubmit = useCallback(() => {
        if (shouldValidateEmail) {
            if (!validator.validate(email)) {
                setShouldValidateEmail(false);
                setIsInvalidEmail(true);
                if (email) {
                    // not empty string
                    setEmailErrorMessage('Please type a valid email.');
                } else {
                    setEmailErrorMessage('Please type an email.');
                }

                sendMetric({
                    action: SignInWithEmailMetrics.ViewErrorMessage,
                    element: 'ErrorMessage',
                    initiator: EVENT_INITIATORS.USER,
                    interactionType: INTERACTION_TYPES.VIEW,
                });
            } else {
                setIsInvalidEmail(false);
                onSubmitEmail(email);
            }
        }
    }, [email, onSubmitEmail, sendMetric, shouldValidateEmail]);

    const enterFunction = useCallback(
        (event) => {
            if (event.keyCode === ENTER_KEY) {
                onSubmit();
            }
        },
        [onSubmit]
    );

    useEffect(() => {
        sendMetric({
            element: 'BaseSocialLoginButtons',
            action: SignInWithEmailMetrics.View,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
    }, [sendMetric]);

    useEffect(() => {
        document.addEventListener('keydown', enterFunction, false);

        return () =>
            document.removeEventListener('keydown', enterFunction, false);
    }, [enterFunction]);

    const onEmailChange = (e) => {
        setShouldValidateEmail(true);
        setEmail(e.target.value);
    };

    return (
        <div className={className}>
            <EmailHeader>Email Address</EmailHeader>
            <MetricsClickable
                action={SignInWithEmailMetrics.ClickTextbox}
                element="EmailTextbox"
            >
                <StyledTextbox
                    data-test="email-form-textbox"
                    placeholder={'Email address'}
                    onChange={onEmailChange}
                    type={'email'}
                    inputMode={'email'}
                    autoComplete={'email'}
                />
            </MetricsClickable>
            {isInvalidEmail && (
                <ErrorMessage data-test="email-form-error-message">
                    {emailErrorMessage}
                </ErrorMessage>
            )}
            <MetricsClickable
                action={SignInWithEmailMetrics.ClickContinue}
                element="ContinueButton"
            >
                <StyledButton
                    data-test="email-form-continue-button"
                    variation={VARIATION.PRIMARY}
                    onClick={onSubmit}
                >
                    Continue
                </StyledButton>
            </MetricsClickable>
        </div>
    );
};

export const SignInWithEmail = withCustomMetrics({
    component: 'SignInWithEmail',
})(BaseSignInWithEmail);

const EmailHeader = styled.h3`
    ${labelSmall};
    margin-bottom: 4px;
    text-align: left;
`;

const StyledTextbox = styled(Textbox)`
    &:focus::placeholder {
        color: ${slate30};
    }
    margin-bottom: 16px;
`;

const StyledButton = styled(Button)`
    font-size: 15px;
    line-height: 18px;
    padding: 11px 0;
    width: 100%;
`;

const ErrorMessage = styled.div`
    ${paragraphMedium};
    color: ${negativeMedium};
    margin-bottom: 3px;
    margin-top: -5px;
    text-align: left;
`;
